import { VendedorQuota, CadastroRow, CotaDiaria } from '../types/wizard';
import { authenticatedFetch } from './authApi';

/**
 * Base URL for the optimizer API
 */
const OPTIMIZER_API_BASE = process.env.NODE_ENV === 'development' 
  ? (process.env.VITE_API_BASE_URL_DEV || 'http://localhost:8000/api')
  : (process.env.VITE_API_BASE_URL_PROD || 'https://dexcobackend-v1-production.up.railway.app/api');

/**
 * Optimizer API request types - NEW FORMAT
 */
export interface OptimizerCotaVendedor {
  no_vendedor: string;
  filial: string;
  data: string; // YYYY-MM-DD format
  cota: number;
}

export interface OptimizerLimiteCliente {
  codigo: string;
  nome: string;
  vendedor: string;
  limite: number;
}

export interface OptimizerParams {
  cota_vendedores: OptimizerCotaVendedor[];
  limite_clientes: OptimizerLimiteCliente[];
}

/**
 * API response types
 */
export interface OptimizerApiError {
  message: string;
  details?: string[];
}

export interface OptimizerTaskResponse {
  task_id: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "ERROR";
}

export interface OptimizerProgressResponse {
  task_id: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "ERROR";
  progress?: {
    percentage: number;
    current_step: string;
  };
  error?: {
    message: string;
  };
}

export interface UpdateOptimizerRequest {
  task_id: string;
  action: "confirm";
  confirmed_orders: string[];
  rejected_orders?: string[];
  modifications?: Array<{
    ordem: string;
    new_date: string;
    reason: string;
  }>;
  notes?: string;
}

export interface OptimizerApiResponse {
  success: boolean;
  error?: OptimizerApiError;
  csvData?: string;
  task_id?: string;
}

/**
 * Convert wizard data to optimizer format - UPDATED WITH DAILY QUOTAS
 */
export const convertToOptimizerParams = (
  dailyQuotas: CotaDiaria[], // Use daily quotas instead of vendor quotas
  cadastroData: CadastroRow[]
): OptimizerParams => {
  // Validate required data
  if (!dailyQuotas || dailyQuotas.length === 0) {
    throw new Error('cota_diaria deve conter pelo menos 1 item');
  }
  
  if (!cadastroData || cadastroData.length === 0) {
    throw new Error('limite_clientes deve conter pelo menos 1 item');
  }

  // Use daily quotas directly (already in correct format)
  const cota_vendedores: OptimizerCotaVendedor[] = dailyQuotas.map(quota => ({
    no_vendedor: quota.no_vendedor,
    filial: quota.filial,
    data: quota.data,
    cota: quota.cota // Daily quota in m³
  }));

  // Convert cadastro to client limits - single entry per client (not repeated by day)
  // Filter out clients with infinite limits
  const limite_clientes: OptimizerLimiteCliente[] = cadastroData
    .filter(row => {
      // Must have client name and volume limit
      if (!row.nomeCliente || !row.limiteVolume) return false;
      
      // Filter out infinite limits
      if (typeof row.limiteVolume === 'string') {
        const limiteStr = row.limiteVolume.toLowerCase();
        if (limiteStr === 'infinito' || limiteStr.includes('infinitas') || limiteStr.includes('sem limite')) {
          return false; // Don't include infinite limits in payload
        }
      }
      
      return true;
    })
    .map(row => {
      // Parse limite from cadastro
      let limite = 0;
      if (typeof row.limiteVolume === 'number') {
        limite = row.limiteVolume;
      } else if (typeof row.limiteVolume === 'string') {
        // Try to parse numeric value
        const parsed = parseFloat(row.limiteVolume);
        limite = isNaN(parsed) ? 0 : parsed;
      }

      return {
        codigo: row.codigoCliente,
        nome: row.nomeCliente,
        vendedor: row.nomeVendedor,
        limite
      };
    })
    .filter(client => client.limite > 0); // Final filter to ensure positive limits only

  // Build simplified payload - backend only considers cota_vendedores and limite_clientes
  const payload: OptimizerParams = {
    cota_vendedores,
    limite_clientes
  };

  // Log payload structure for debugging
  console.log('📊 Optimizer Payload Structure:', {
    cota_vendedores_count: cota_vendedores.length,
    limite_clientes_count: limite_clientes.length,
    unique_vendors: [...new Set(cota_vendedores.map(c => c.no_vendedor))],
    date_range: {
      first: cota_vendedores[0]?.data,
      last: cota_vendedores[cota_vendedores.length - 1]?.data
    },
    sample_quotas: cota_vendedores.slice(0, 3)
  });

  return payload;
};

/**
 * Step 1: Start async optimization - POST /set_optimizer_params/
 */
export const startOptimization = async (
  dailyQuotas: CotaDiaria[],
  cadastroData: CadastroRow[]
): Promise<OptimizerTaskResponse> => {
  try {
    // Convert to API format (includes validation)
    const params = convertToOptimizerParams(dailyQuotas, cadastroData);
    
    console.log('🚀 Starting optimization with params:', params);
    
    const response = await authenticatedFetch(`${OPTIMIZER_API_BASE}/set_optimizer_params/`, {
      method: 'POST',
      body: JSON.stringify(params)
    });

    console.log('📥 Optimization start response status:', response.status);

    if (response.status === 201) {
      const responseData = await response.json();
      console.log('✅ Optimization started, uuid:', responseData.uuid);
      return {
        task_id: responseData.uuid,
        status: 'PENDING'
      };
    }

    // Handle error responses
    let errorMessage = 'Erro desconhecido ao iniciar otimização';
    
    if (response.status === 400) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || 'Erro de validação dos parâmetros';
      } catch (parseError) {
        const textError = await response.text();
        errorMessage = textError || 'Erro de validação dos parâmetros';
      }
    } else {
      errorMessage = `Erro HTTP ${response.status}: ${response.statusText}`;
    }

    console.error('❌ Optimization start error:', { status: response.status, message: errorMessage });
    throw new Error(errorMessage);

  } catch (error) {
    console.error('❌ Network error starting optimization:', error);
    
    // Mock response for development when backend is not available
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Using mock response for development');
      return {
        task_id: 'mock-task-' + Date.now(),
        status: 'PENDING'
      };
    }
    
    throw error;
  }
};

/**
 * Step 2: Poll optimization progress - GET /get_optimizer_results/
 */
export const pollOptimizationProgress = async (task_id: string): Promise<OptimizerProgressResponse> => {
  try {
    console.log('🔄 Polling optimization progress for task:', task_id);
    
    const response = await authenticatedFetch(`${OPTIMIZER_API_BASE}/get_optimizer_results/?request_uuid=${task_id}`);

    console.log('📥 Polling response status:', response.status);

    if (response.status === 202) {
      // Still processing - PENDING or RUNNING
      const progressData = await response.json();
      console.log('⏳ Optimization in progress:', progressData);
      return progressData;
    }
    
    if (response.status === 200) {
      // Completed - return CSV data
      const csvData = await response.text();
      console.log('✅ Optimization completed, CSV length:', csvData.length);
      return {
        task_id,
        status: 'COMPLETED',
        csvData
      } as OptimizerProgressResponse & { csvData: string };
    }
    
    if (response.status === 500) {
      // Error occurred
      const errorData = await response.json();
      console.error('❌ Optimization failed:', errorData);
      return {
        task_id,
        status: 'ERROR',
        error: {
          message: errorData.error?.message || 'Erro interno do otimizador'
        }
      };
    }

    // Unexpected status
    throw new Error(`Status inesperado: ${response.status}`);

  } catch (error) {
    console.error('❌ Network error polling optimization:', error);
    
    // Mock response for development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Using mock polling response for development');
      
      // Simulate different stages based on time
      const elapsed = Date.now() - parseInt(task_id.replace('mock-task-', ''));
      
      if (elapsed < 5000) {
        return {
          task_id,
          status: 'PENDING',
          progress: {
            percentage: 10,
            current_step: 'Inicializando otimização...'
          }
        };
      } else if (elapsed < 10000) {
        return {
          task_id,
          status: 'RUNNING',
          progress: {
            percentage: 50,
            current_step: 'Processando dados de vendedores...'
          }
        };
      } else if (elapsed < 15000) {
        return {
          task_id,
          status: 'RUNNING',
          progress: {
            percentage: 80,
            current_step: 'Gerando cronograma otimizado...'
          }
        };
      } else {
        // Load mock CSV
        try {
          const csvResponse = await fetch('/payload_example.csv');
          const csvData = await csvResponse.text();
          return {
            task_id,
            status: 'COMPLETED',
            csvData
          } as OptimizerProgressResponse & { csvData: string };
        } catch {
          return {
            task_id,
            status: 'COMPLETED',
            csvData: 'mock,csv,data\n1,2,3'
          } as OptimizerProgressResponse & { csvData: string };
        }
      }
    }
    
    throw error;
  }
};

/**
 * Step 3: Update optimizer results - POST /update_optimizer_results/
 */
export const updateOptimizerResults = async (
  task_id: string, 
  updateData: UpdateOptimizerRequest
): Promise<void> => {
  try {
    console.log('💾 Updating optimizer results for task:', task_id);
    
    const response = await authenticatedFetch(`${OPTIMIZER_API_BASE}/update_optimizer_results/`, {
      method: 'POST',
      body: JSON.stringify(updateData)
    });

    console.log('📥 Update response status:', response.status);

    if (response.status === 200) {
      console.log('✅ Optimizer results updated successfully');
      return;
    }

    // Handle error responses
    let errorMessage = 'Erro ao salvar alterações';
    
    if (response.status >= 400 && response.status < 500) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        const textError = await response.text();
        errorMessage = textError || errorMessage;
      }
    } else {
      errorMessage = `Erro HTTP ${response.status}: ${response.statusText}`;
    }

    console.error('❌ Update optimizer results error:', { status: response.status, message: errorMessage });
    throw new Error(errorMessage);

  } catch (error) {
    console.error('❌ Network error updating optimizer results:', error);
    
    // Mock response for development
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Using mock update response for development');
      return;
    }
    
    throw error;
  }
};

/**
 * Legacy function - kept for compatibility but now deprecated
 */
export const getOptimizedResults = async (uuid?: string): Promise<OptimizerApiResponse> => {
  try {
    console.log('📤 Requesting optimized results...', uuid ? `uuid: ${uuid}` : '');
    
    const url = uuid 
      ? `${OPTIMIZER_API_BASE}/get_optimizer_results/?request_uuid=${uuid}`
      : `${OPTIMIZER_API_BASE}/get_optimizer_results/`;
    
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'text/csv'
      }
    });

    console.log('📥 Optimized results response status:', response.status);

    if (response.ok) {
      // Check if response is CSV
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('text/csv')) {
        console.warn('⚠️ Expected CSV but got:', contentType);
      }

      // Get CSV data as text
      const csvData = await response.text();
      
      if (!csvData || csvData.trim().length === 0) {
        console.error('❌ Empty CSV response');
        return {
          success: false,
          error: {
            message: 'Arquivo CSV vazio recebido do otimizador'
          }
        };
      }

      console.log('✅ Optimized results received successfully');
      return { 
        success: true, 
        csvData: csvData 
      };
    }

    // Handle error responses
    let errorMessage = 'Erro ao obter resultados otimizados';
    
    if (response.status === 400) {
      const errorText = await response.text();
      errorMessage = errorText || 'Erro de solicitação ao otimizador';
    } else if (response.status === 404) {
      errorMessage = 'Resultados otimizados não encontrados. Execute primeiro a configuração de parâmetros.';
    } else if (response.status >= 500) {
      errorMessage = 'Erro interno do servidor de otimização';
    } else {
      errorMessage = `Erro HTTP ${response.status}: ${response.statusText}`;
    }

    console.error('❌ Optimized results error:', { status: response.status, message: errorMessage });

    return {
      success: false,
      error: {
        message: errorMessage
      }
    };

  } catch (error) {
    console.error('❌ Network error getting optimized results:', error);
    
    // Mock response for development when backend is not available
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Using mock response for development');
      return {
        success: true,
        csvData: `id,ordem,item,centro,cli_cod_pai,cli_razao_social,no_vendedor,cd_produto,prd_dc_linha,prd_espessura,prd_tx_breve,qt_vol_m3_liquido_gerencial,dt_emissao,dt_desejada_remessa,dt_producao
1,ORD001,ITEM001,CTR001,12345,"Cliente ABC","Vendedor 1",PROD001,"Produto A",0.18,"Produto A",45.6,2024-01-15,2024-01-20,2024-01-16
2,ORD002,ITEM002,CTR002,23456,"Cliente XYZ","Vendedor 2",PROD002,"Produto B",0.20,"Produto B",32.8,2024-01-15,2024-01-21,2024-01-17`
      };
    }
    
    const errorMessage = error instanceof Error 
      ? `Erro de conexão: ${error.message}`
      : 'Erro de rede ao obter resultados otimizados';

    return {
      success: false,
      error: {
        message: errorMessage
      }
    };
  }
};



/**
 * Legacy function - kept for compatibility but deprecated
 * Use startOptimization + pollOptimizationProgress instead
 */
export const runOptimizerWorkflow = async (
  _vendorQuotas: VendedorQuota[],
  _cadastroData: CadastroRow[],
  _maxDailyVolume?: number,
  _targetDate?: string
): Promise<OptimizerApiResponse> => {
  console.log('🚀 Starting legacy optimizer workflow...', { _maxDailyVolume });
  
  // For backward compatibility, return a mock response
  if (process.env.NODE_ENV === 'development') {
    return {
      success: true,
      csvData: 'mock,csv,data\n1,2,3',
      task_id: 'legacy-mock-' + Date.now()
    };
  }
  
  throw new Error('runOptimizerWorkflow is deprecated. Use startOptimization + pollOptimizationProgress instead.');
};

/**
 * Legacy wrapper for updateOptimizerResults - for backward compatibility
 */
export const updateOptimizerResultsLegacy = async (
  task_id: string, 
  csvFile: File
): Promise<OptimizerApiResponse> => {
  try {
    const updateData: UpdateOptimizerRequest = {
      task_id,
      action: "confirm",
      confirmed_orders: [], // Will be populated from CSV if needed
      notes: `CSV file uploaded: ${csvFile.name}`
    };

    await updateOptimizerResults(task_id, updateData);
    
    return {
      success: true,
      csvData: 'Resultados atualizados com sucesso'
    };
  } catch (error) {
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : 'Erro ao atualizar resultados'
      }
    };
  }
}; 